import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { errorMessage } from '../../services/errors';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import { createPerson, listPeople } from '../../services/people';
import { inputClass, outlineButton, primaryButton } from '../../components/styles';

export default function PeoplePage() {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.peopleList(search),
    queryFn: async () => {
      const people = await listPeople(search);
      return { people, full: reachedLimit(people) };
    },
  });
  const people = data?.people ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <Users className="w-8 h-8 text-primary" />
          <h1 className="text-2xl font-bold">People</h1>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className={`${primaryButton} inline-flex items-center gap-1`}>
            <Plus className="w-4 h-4" aria-hidden="true" /> Add person
          </button>
        )}
      </div>

      {adding && <AddPersonForm onCancel={() => setAdding(false)} />}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-on-surface-variant" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search people..."
            aria-label="Search people"
            className="w-full min-h-11 pointer-fine:min-h-0 pl-10 pr-4 py-2 text-base rounded-xl border border-outline/30 bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {data?.full && (
        <p className="text-xs text-tertiary">
          Only the first {LIST_LIMIT.toLocaleString()} people (most recently in touch first) were read, so this list may be incomplete.
        </p>
      )}

      {/* List */}
      {isError ? (
        <p className="text-sm text-error">Could not load people: {errorMessage(error)}</p>
      ) : isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-surface rounded-xl p-4 h-24" />
          ))}
        </div>
      ) : people.length === 0 ? (
        <div className="text-center py-12 text-on-surface-variant">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{search ? 'No people found' : 'No people yet. Add someone, or mention them in a capture.'}</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {people.map((person) => (
            <Link
              key={person.id}
              to={`/people/${person.id}`}
              className="block bg-surface rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-container flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold text-lg">{person.name.trim().charAt(0).toUpperCase() || '?'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold break-words">{person.name}</h3>
                  {person.context && <p className="text-sm text-on-surface-variant truncate">{person.context}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-on-surface-variant">
                    <span>{person.openFollowUps} follow-ups</span>
                    {person.last_touched && <span>{format(new Date(person.last_touched), 'MMM d')}</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** Add a person by hand: a name (required) and how you know them (optional). Opens their page once saved. */
function AddPersonForm({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [context, setContext] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createPerson(name, context),
    onSuccess: (person) => {
      queryClient.setQueryData(queryKeys.person(person.id), person);
      navigate(`/people/${person.id}`);
      return Promise.all([queryKeys.people, queryKeys.searches].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidation('A person needs a name.');
      return;
    }
    setValidation(null);
    mutation.mutate();
  };

  return (
    <form onSubmit={submit} aria-label="Add a person" className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
      <label className="block space-y-1">
        <span className="text-xs font-medium text-on-surface-variant">Name</span>
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" autoFocus required />
      </label>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-on-surface-variant">Context (optional)</span>
        <input
          className={inputClass}
          value={context}
          onChange={(e) => setContext(e.target.value)}
          aria-label="Context"
          placeholder="How do you know this person?"
        />
      </label>
      {validation && <p className="text-sm text-error">{validation}</p>}
      {mutation.isError && <p className="text-sm text-error">Not added: {errorMessage(mutation.error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          {mutation.isPending ? 'Adding…' : 'Add'}
        </button>
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className={outlineButton}>
          Cancel
        </button>
      </div>
    </form>
  );
}
